import { formatCandidatePhone } from '../utils/candidateDisplay';
import { appCurrentStatusLabel } from '../utils/applicationCurrentStatus';

export const CANDIDATE_SORT_COLUMNS = [
  { key: 'id', type: 'id', getValue: c => c.id },
  {
    key: 'name',
    type: 'string',
    getValue: c => c.displayName || c.personal?.name || c.contact?.name || ''
  },
  { key: 'mobile', type: 'string', getValue: c => formatCandidatePhone(c) },
  { key: 'email', type: 'string', getValue: c => c.displayEmail || c.contact?.email || '' },
  { key: 'location', type: 'string', getValue: c => c.displayLocation || c.location || '' },
  {
    key: 'experience',
    type: 'number',
    getValue: c => c.experienceYears ?? c.experience ?? ''
  },
  { key: 'status', type: 'string', getValue: c => c.status || '' },
  {
    key: 'skills',
    type: 'number',
    getValue: c => c.skillCount ?? (Array.isArray(c.skills) ? c.skills.length : 0)
  }
];

export const APPLICATION_SORT_COLUMNS = [
  { key: 'id', type: 'id', getValue: a => a.id },
  { key: 'jobTitle', type: 'string', getValue: a => a.jobTitle || a.jobId || '' },
  {
    key: 'candidate',
    type: 'string',
    getValue: a => a.candidateName || a.candidateId || ''
  },
  { key: 'mobile', type: 'string', getValue: a => a.candidatePhone || '' },
  { key: 'email', type: 'string', getValue: a => a.candidateEmail || '' },
  { key: 'client', type: 'string', getValue: a => a.clientName || '' },
  { key: 'stage', type: 'string', getValue: a => a.stage || '' },
  { key: 'status', type: 'string', getValue: a => appCurrentStatusLabel(a) },
  { key: 'notes', type: 'string', getValue: a => a.statusNotes || '' },
  { key: 'date', type: 'date', getValue: a => a.createdAt || a.updatedAt || '' }
];

export const JOB_SORT_COLUMNS = [
  { key: 'id', type: 'id', getValue: j => j.id },
  { key: 'title', type: 'string', getValue: j => j.title || '' },
  { key: 'client', type: 'string', getValue: j => j.clientName || j.clientId || '' },
  { key: 'location', type: 'string', getValue: j => j.location || '' },
  {
    key: 'budget',
    type: 'string',
    getValue: j => `${j.budgetFrom || ''}-${j.budgetTo || ''}`
  },
  {
    key: 'staffing',
    type: 'number',
    getValue: j =>
      j.balanceToRecruit ??
      Math.max(0, (j.numberOfPositions || 1) - (j.joinedCount || 0))
  },
  { key: 'status', type: 'string', getValue: j => j.status || '' },
  { key: 'recruiter', type: 'string', getValue: j => j.recruiterName || j.recruiterId || '' }
];

export const CLIENT_SORT_COLUMNS = [
  { key: 'id', type: 'id', getValue: c => c.id },
  { key: 'name', type: 'string', getValue: c => c.name || '' },
  {
    key: 'contacts',
    type: 'number',
    getValue: c => (c.contacts || []).length
  },
  { key: 'paymentTerms', type: 'string', getValue: c => c.paymentTerms || '' },
  { key: 'status', type: 'string', getValue: c => c.status || '' }
];

export const RECRUITER_SORT_COLUMNS = [
  { key: 'id', type: 'id', getValue: r => r.id },
  { key: 'name', type: 'string', getValue: r => r.name || '' },
  { key: 'email', type: 'string', getValue: r => r.email || '' },
  { key: 'phone', type: 'string', getValue: r => r.phone || '' },
  { key: 'status', type: 'string', getValue: r => r.status || '' }
];

export const USER_SORT_COLUMNS = [
  { key: 'id', type: 'id', getValue: u => u.id },
  { key: 'name', type: 'string', getValue: u => u.name || '' },
  { key: 'email', type: 'string', getValue: u => u.email || '' },
  { key: 'role', type: 'string', getValue: u => u.role || '' }
];

export const INVOICE_SORT_COLUMNS = [
  { key: 'id', type: 'id', getValue: i => i.id },
  { key: 'client', type: 'string', getValue: i => i.clientName || i.clientId || '' },
  { key: 'application', type: 'id', getValue: i => i.applicationId || '' },
  { key: 'status', type: 'string', getValue: i => i.status || '' },
  { key: 'terms', type: 'string', getValue: i => i.paymentTerms || i.terms || '' }
];

export const REFERRAL_SORT_COLUMNS = [
  { key: 'id', type: 'id', getValue: r => r.id },
  {
    key: 'referred',
    type: 'string',
    getValue: r => r.referredName || r.referredEmail || ''
  },
  { key: 'job', type: 'string', getValue: r => r.jobTitle || r.jobId || '' },
  { key: 'client', type: 'string', getValue: r => r.clientName || '' },
  { key: 'referrer', type: 'string', getValue: r => r.referrerName || '' },
  { key: 'date', type: 'date', getValue: r => r.createdAt || '' }
];

export const MY_APPLICATION_SORT_COLUMNS = [
  { key: 'id', type: 'id', getValue: a => a.id },
  { key: 'jobTitle', type: 'string', getValue: a => a.jobTitle || a.jobId || '' },
  { key: 'candidate', type: 'string', getValue: a => a.candidateName || a.candidateId || '' },
  { key: 'client', type: 'string', getValue: a => a.clientName || '' },
  { key: 'stage', type: 'string', getValue: a => a.stage || '' },
  {
    key: 'offer',
    type: 'string',
    getValue: a => a.offerStatusLabel || a.offerStatus || appCurrentStatusLabel(a)
  },
  { key: 'date', type: 'date', getValue: a => a.createdAt || a.updatedAt || '' }
];

export const SUITABLE_JOB_MATCH_COLUMNS = [
  { key: 'id', type: 'id', getValue: row => row.job?.id || row.id },
  { key: 'title', type: 'string', getValue: row => row.job?.title || '' },
  { key: 'client', type: 'string', getValue: row => row.job?.clientName || '' },
  { key: 'location', type: 'string', getValue: row => row.job?.location || '' },
  {
    key: 'match',
    type: 'number',
    getValue: row => row.matchPercent ?? row.matchScore ?? 0
  },
  {
    key: 'skills',
    type: 'number',
    getValue: row => row.skillsFitPercent ?? row.skillsMatch ?? 0
  }
];

export const OPENING_JOB_COLUMNS = [
  { key: 'id', type: 'id', getValue: j => j.id },
  { key: 'title', type: 'string', getValue: j => j.title || '' },
  { key: 'client', type: 'string', getValue: j => j.clientName || '' },
  { key: 'location', type: 'string', getValue: j => j.location || '' }
];

export const REFERRAL_LIST_COLUMNS = [
  { key: 'id', type: 'id', getValue: r => r.id },
  { key: 'name', type: 'string', getValue: r => r.referredName || '' },
  { key: 'email', type: 'string', getValue: r => r.referredEmail || '' },
  { key: 'job', type: 'string', getValue: r => r.jobTitle || '' },
  { key: 'client', type: 'string', getValue: r => r.clientName || '' },
  { key: 'date', type: 'date', getValue: r => r.createdAt || '' }
];

export const COMPANY_SORT_COLUMNS = [
  { key: 'id', type: 'id', getValue: c => c.id },
  { key: 'name', type: 'string', getValue: c => c.name || '', defaultDir: 'asc' },
  { key: 'domain', type: 'string', getValue: c => c.domain || '' },
  { key: 'created', type: 'date', getValue: c => c.createdAt || '' }
];
